import {AxiosError} from 'axios'
import axiosInstance from './AxiosInstance'

const LINK_INFORMATION_PATH = '/thiredparty/v1/link-information'

export type LinkInformationLocation = {
  lat: number
  lng: number
  name?: string
}

export type LinkInformationData = {
  transporterName?: string
  requestId?: string
  vehicleRegistrationNo?: string
  gpsVehicleCode: number
  driverName?: string
  driverContact?: string
  locations: LinkInformationLocation[]
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function parseLocations(raw: unknown): LinkInformationLocation[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const out: LinkInformationLocation[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') {
      continue
    }
    const o = row as Record<string, unknown>
    const lat = num(o.Lat ?? o.lat)
    const lng = num(o.Lng ?? o.lng)
    if (lat === undefined || lng === undefined) {
      continue
    }
    const name =
      typeof o.Name === 'string'
        ? o.Name
        : typeof o.name === 'string'
          ? o.name
          : undefined
    out.push({lat, lng, name})
  }
  return out
}

function parsePayload(data: unknown): LinkInformationData | null {
  if (!data || typeof data !== 'object') {
    return null
  }
  const root = data as Record<string, unknown>
  const payload =
  root.result && typeof root.result === 'object'
    ? (root.result as Record<string, unknown>)
    : root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root

  const codeRaw = payload.GPSVehicleCode ?? payload.gpsVehicleCode
  const gpsVehicleCode = num(codeRaw)
  if (gpsVehicleCode === undefined) {
    return null
  }

  const locations = parseLocations(payload.Locations ?? payload.locations)

  return {
    transporterName:
      typeof payload.TransporterName === 'string'
        ? payload.TransporterName
        : typeof payload.transporterName === 'string'
          ? payload.transporterName
          : undefined,
    requestId:
      typeof payload.requestId === 'string'
        ? payload.requestId
        : typeof payload.requestId === 'string'
          ? payload.requestId
          : undefined,
    vehicleRegistrationNo:
      typeof payload.VehicleRegistrationNo === 'string'
        ? payload.VehicleRegistrationNo
        : typeof payload.vehicleRegistrationNo === 'string'
          ? payload.vehicleRegistrationNo
          : undefined,
    gpsVehicleCode,
    driverName:
      typeof payload.DriverName === 'string'
        ? payload.DriverName
        : typeof payload.driverName === 'string'
          ? payload.driverName
          : undefined,
    driverContact:
      typeof payload.DriverContact === 'string'
        ? payload.DriverContact
        : typeof payload.driverContact === 'string'
          ? payload.driverContact
          : undefined,
    locations,
  }
}

/**
 * POST /thiredparty/v1/link-information — job details for active public link.
 */
export async function postLinkInformation(jobKey: string): Promise<LinkInformationData> {
  const trimmed = jobKey.trim()
  if (!trimmed) {
    throw new Error('Missing job key')
  }

  try {
    const {data} = await axiosInstance.post<unknown>(LINK_INFORMATION_PATH, {jobKey: trimmed})
    const parsed = parsePayload(data)
    if (!parsed) {
      throw new Error('Invalid link information response')
    }
    return parsed
  } catch (err) {
    const ax = err as AxiosError<{message?: string}>
    const msg =
      (ax.response?.data &&
        typeof ax.response.data === 'object' &&
        typeof (ax.response.data as {message?: string}).message === 'string' &&
        (ax.response.data as {message: string}).message) ||
      ax.message ||
      'Failed to load job information'
    throw new Error(msg)
  }
}
